const parsePersistedTopology = (value) => {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const isObjectRecord = (value) =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const isPanelIndex = (value) => {
  if (
    (typeof value !== 'number' && typeof value !== 'string') ||
    (typeof value === 'string' && !value.trim())
  ) {
    return false;
  }
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric >= 0;
};

const isPanelReference = (value) =>
  Array.isArray(value) &&
  value.length >= 2 &&
  value.slice(0, 2).every(isPanelIndex);

const hasPanelReferences = (value) =>
  Array.isArray(value) && value.length > 0 && value.every(isPanelReference);

const isStringRecord = (value) =>
  isObjectRecord(value) && hasPanelReferences(value.panels);

export const hasUsableStringingData = (value) => {
  const parsed = parsePersistedTopology(value);
  if (Array.isArray(parsed)) {
    return parsed.length > 0 && parsed.every(isStringRecord);
  }
  if (!isObjectRecord(parsed)) return false;

  const keys = Object.keys(parsed);
  return (
    keys.length > 0 &&
    keys.every((key) => /^string_\d+$/.test(key)) &&
    keys.every((key) => hasPanelReferences(parsed[key]))
  );
};

const hasInverterIdentity = (value) => {
  if (!isObjectRecord(value)) return false;
  const identity =
    value.hardware_id ?? value.hardwareId ?? value.id ?? value.model;
  if (
    !(
      (typeof identity === 'string' && identity.trim().length > 0) ||
      (typeof identity === 'number' &&
        Number.isFinite(identity) &&
        identity > 0)
    )
  ) {
    return false;
  }
  if (value.count !== undefined && !(Number(value.count) > 0)) return false;
  return true;
};

const isLegacyInverterReference = (value) =>
  (typeof value === 'string' && value.trim().length > 0) ||
  (typeof value === 'number' && Number.isFinite(value) && value > 0) ||
  hasInverterIdentity(value);

export const hasUsableInvertersUsed = (value) => {
  const parsed = parsePersistedTopology(value);
  if (Array.isArray(parsed)) {
    return parsed.length > 0 && parsed.every(hasInverterIdentity);
  }
  if (!isObjectRecord(parsed)) return false;

  const keys = Object.keys(parsed);
  const allowedKeys = new Set(['primary', 'additional', 'inverters']);
  if (keys.length === 0 || keys.some((key) => !allowedKeys.has(key))) {
    return false;
  }
  return keys.every((key) => {
    const entry = parsed[key];
    return Array.isArray(entry)
      ? entry.length > 0 && entry.every(isLegacyInverterReference)
      : isLegacyInverterReference(entry);
  });
};

export const countSelectedPanels = (selectedPanels = {}) => {
  if (!selectedPanels || typeof selectedPanels !== 'object') return 0;

  return Object.values(selectedPanels).reduce(
    (sum, roofPanels) =>
      sum +
      (Array.isArray(roofPanels)
        ? roofPanels.reduce(
            (roofSum, value) => roofSum + (Number(value) > 0 ? 1 : 0),
            0
          )
        : 0),
    0
  );
};

export const getProductionReadiness = ({
  solarProduction = 0,
  totalPanels = 0,
  stringingData = null,
  invertersUsed = null,
  isLegacyProposal = false
} = {}) => {
  const hasAnnualProduction = Number(solarProduction || 0) > 0;
  const hasPanelCount = Number(totalPanels || 0) > 0;
  const hasStringingData = hasUsableStringingData(stringingData);
  const hasInvertersUsed = hasUsableInvertersUsed(invertersUsed);
  const hasLegacyProductionIndicator =
    hasPanelCount || hasStringingData || hasInvertersUsed;
  const hasCompleteModernTopology =
    hasPanelCount && hasStringingData && hasInvertersUsed;

  return {
    hasAnnualProduction,
    hasPanelCount,
    hasStringingData,
    hasInvertersUsed,
    hasPersistedProduction:
      hasAnnualProduction &&
      (isLegacyProposal
        ? hasLegacyProductionIndicator
        : hasCompleteModernTopology),
    hasSolarProductionOnly:
      isLegacyProposal && hasAnnualProduction && !hasLegacyProductionIndicator,
    shouldRecalculateModernProduction:
      !isLegacyProposal && hasAnnualProduction && !hasCompleteModernTopology
  };
};
